import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, Button, Space } from "antd";
import { Button as UIButton } from "@pc/ds-react";
import { usePopup } from "../../hooks/usePopup";
import { useTranslation } from "../../hooks/useTranslation";
import { PopupType } from "../../redux/slices/popupSlice/popupSlice";
import { useAppSelector, useAppDispatch } from "src/store/hooks";
import {
  clearPermissionErrorModal,
  clearEligibilityErrorModal,
  type EligibilityErrorModalContent,
  type EligibilityErrorInlineLink,
} from "src/redux/slices/userSlice/UserSlice";
import {
  updateGroupMailingConfirmationApi,
} from "src/redux/slices/userSlice/UserApi";
import { logPfsAnalyticsEvent } from "@/utils/helpers";
import { API_RESULT, EVENT_NAME, EVENT_SCREEN } from "@/utils/enums";
import { UIModal } from "src/UIKit";
import TransferDisclaimerModal from "../TransferDisclaimerModal";
import SavingsStartModal from "../SavingsStartModal/SavingsStartModal";
import "./PopupManager.scss";
import { routes } from "src/navigation/routePaths";
import { saveLoginReturnTo } from "src/navigation/loginReturnTo";

const ELIGIBILITY_MEGAPHONE_IMG_SRC = "/images/megaphone.png";

const hasEligibilityCustomBody = (modal: EligibilityErrorModalContent): boolean =>
  !!(modal.bodyLink || modal.bodyBeforeLink || modal.inlineLinks?.length || modal.body.includes("\n"));

const renderEligibilityBodyParagraphs = (text: string) =>
  text.split("\n").map((line, index) => (
    <p key={index} className="ui-modal__body">
      {line}
    </p>
  ));

const renderBodyWithInlineLinks = (
  text: string,
  links: EligibilityErrorInlineLink[],
): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let keyIdx = 0;

  while (remaining.length > 0) {
    let earliestIndex = -1;
    let earliestLink: EligibilityErrorInlineLink | null = null;

    for (const link of links) {
      const idx = remaining.indexOf(link.placeholder);
      if (idx !== -1 && (earliestIndex === -1 || idx < earliestIndex)) {
        earliestIndex = idx;
        earliestLink = link;
      }
    }

    if (earliestIndex === -1 || !earliestLink) {
      parts.push(<span key={keyIdx++}>{remaining}</span>);
      break;
    }

    if (earliestIndex > 0) {
      parts.push(<span key={keyIdx++}>{remaining.slice(0, earliestIndex)}</span>);
    }

    parts.push(
      <a
        key={keyIdx++}
        href={earliestLink.href}
        style={{ textDecoration: "underline" }}
      >
        {earliestLink.label}
      </a>,
    );

    remaining = remaining.slice(earliestIndex + earliestLink.placeholder.length);
  }

  return parts;
};

const PopupManager: React.FC = () => {
  const { currentPopup, close } = usePopup();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const permissionErrorModal = useAppSelector(
    (state) => state.user.permissionErrorModal,
  );
  const eligibilityErrorModal = useAppSelector(
    (state) => state.user.eligibilityErrorModal,
  );
  const savingsConsentRef = useRef(true);

  const transferDisclaimerData = currentPopup?.data?.transferDisclaimer as
    | {
        userName?: string;
        siteName: string;
        siteLogo?: string;
        affiliateAggregatorName: string;
        limitAmount?: number;
        /** Timer base from the clicked card's timerBaseRef entry (serverMs in ms). */
        websiteTimerServerMs?: number;
        /** Wall-clock timestamp (Date.now()) when websiteTimerServerMs was recorded. */
        websiteTimerReceivedAt?: number;
        onConfirmStart?: (popup: Window | null) => void;
        onConfirm: (popup: Window | null) => void;
      }
    | undefined;
  const isTransferDisclaimerPopup = transferDisclaimerData != null;

  const savingsStartData = currentPopup?.data?.savingsStart as
    | {
        remainingUsageAmount: number;
        groupMailingConfirmation?: boolean;
        onConfirm: () => void;
      }
    | undefined;
  const isSavingsStartPopup = savingsStartData != null;


  useEffect(() => {
    if (currentPopup?.autoClose) {
      const timer = setTimeout(() => {
        currentPopup.onClose?.();
        close(currentPopup.id);
      }, currentPopup.autoClose);

      return () => clearTimeout(timer);
    }
  }, [currentPopup, close]);

  const handleClose = () => {
    if (!currentPopup) return;
    if (currentPopup.onClose) {
      currentPopup.onClose();
    }
    close(currentPopup.id);
  };

  const handleConfirm = () => {
    if (!currentPopup) return;
    if (currentPopup.onConfirm) {
      currentPopup.onConfirm();
    }
    close(currentPopup.id);
  };

  const handleCancel = () => {
    if (!currentPopup) return;
    if (currentPopup.onCancel) {
      currentPopup.onCancel();
    }
    close(currentPopup.id);
  };

  const getModalProps = () => {
    if (!currentPopup) return {};
    const baseProps = {
      open: true,
      title: currentPopup.title,
      onCancel: currentPopup.closable !== false ? handleClose : undefined,
      mask: { closable: currentPopup.maskClosable !== false },
      width:
        currentPopup.width ||
        (isTransferDisclaimerPopup ? 800 : 520),
      zIndex: currentPopup.zIndex || 1055,
      footer: null,
      className: `popup-manager popup-manager--${currentPopup.type}`,
    };

    if (isTransferDisclaimerPopup) {
      return baseProps;
    }

    // Custom buttons or default based on type
    if (currentPopup.buttons && currentPopup.buttons.length > 0) {
      return {
        ...baseProps,
        footer: (
          <Space>
            {currentPopup.buttons.map((button, index) => {
              // Map 'danger' to 'primary' for Ant Design Button compatibility
              const buttonType =
                button.type === "danger" ? "primary" : button.type || "default";

              return (
                <Button
                  key={index}
                  type={
                    buttonType as
                      | "default"
                      | "link"
                      | "text"
                      | "primary"
                      | "dashed"
                      | undefined
                  }
                  danger={button.type === "danger"}
                  onClick={() => {
                    if (button.onClick) {
                      button.onClick();
                    }
                    if (
                      currentPopup.type === PopupType.CONFIRM &&
                      index === 1
                    ) {
                      handleConfirm();
                    } else if (
                      currentPopup.type === PopupType.CONFIRM &&
                      index === 0
                    ) {
                      handleCancel();
                    } else {
                      handleClose();
                    }
                  }}
                >
                  {button.text}
                </Button>
              );
            })}
          </Space>
        ),
      };
    }

    // Default buttons based on type
    switch (currentPopup.type) {
      case PopupType.CONFIRM:
        return {
          ...baseProps,
          footer: (
            <Space>
              <Button onClick={handleCancel}>{t("popup.cancel")}</Button>
              <Button type="primary" onClick={handleConfirm}>
                {t("popup.confirm")}
              </Button>
            </Space>
          ),
        };

      case PopupType.ERROR:
      case PopupType.WARNING:
        return {
          ...baseProps,
          footer: (
            <Button type="primary" onClick={handleClose}>
              {t("popup.understood")}
            </Button>
          ),
        };

      default:
        return {
          ...baseProps,
          footer: (
            <Button type="primary" onClick={handleClose}>
              {t("popup.close")}
            </Button>
          ),
        };
    }
  };

  const handleSavingsConfirm = (groupMailingConsent: boolean) => {
    if (!savingsStartData) return;
    if (!savingsStartData.groupMailingConfirmation && groupMailingConsent) {
      dispatch(
        updateGroupMailingConfirmationApi({
          feGroupMailingConfirmation: true,
        }),
      )
        .unwrap()
        .then(() => {
          logPfsAnalyticsEvent({
            event_name: EVENT_NAME.GROUP_MAIL_APPROVAL,
            event_screen: EVENT_SCREEN.LOGIN,
            status: API_RESULT.SUCCEEDED,
          });
        })
        .catch((error) => {
          logPfsAnalyticsEvent({
            event_name: EVENT_NAME.GROUP_MAIL_APPROVAL,
            event_screen: EVENT_SCREEN.LOGIN,
            status: API_RESULT.FAILED,
            failure_reason: error,
          });
        });
    }
    savingsStartData.onConfirm();
    handleClose();
  };

  if (!permissionErrorModal && !currentPopup && !eligibilityErrorModal) {
    return null;
  }

  return (
    <>
      {permissionErrorModal && (
        <UIModal
          open={true}
          onClose={() => dispatch(clearPermissionErrorModal())}
          title={permissionErrorModal.title}
          body={permissionErrorModal.body}
          footer={permissionErrorModal.footer}
          primaryButton={
            permissionErrorModal.primaryButtonLabel
              ? {
                  label: permissionErrorModal.primaryButtonLabel,
                  onClick: () => {
                    dispatch(clearPermissionErrorModal());
                    const returnTo =
                      permissionErrorModal.loginReturnTo ?? pathname;
                    saveLoginReturnTo(returnTo);
                    navigate(routes.login, {
                      state: {
                        returnTo,
                      },
                    });
                  },
                }
              : undefined
          }
        />
      )}
      {eligibilityErrorModal && (() => {
        const modal = eligibilityErrorModal;
        const closeEligibilityModal = () => {
          dispatch(clearEligibilityErrorModal());
          navigate(routes.access, { replace: true });
        };
        const disclaimerNode = modal.showDisclaimer ? (
          <>
            {t("tsEligibility.disclaimer")}{" "}
            <a
              href="https://www.passportcard.co.il"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => logPfsAnalyticsEvent({
                event_name: EVENT_NAME.LINK_CLICK,
                event_screen: EVENT_SCREEN.LOGIN,
                link: { link_url: "https://www.passportcard.co.il", link_name: "passportcard_home" },
              })}
            >
              {t("tsEligibility.disclaimerLink")}
            </a>
          </>
        ) : undefined;
        const primaryButton = {
          label: t("tsEligibility.backToHome"),
          onClick: closeEligibilityModal,
        };

        if (hasEligibilityCustomBody(modal)) {
          return (
            <UIModal
              open={!!modal}
              onClose={closeEligibilityModal}
              title={modal.title}
              className="ui-modal--eligibility"
              primaryButton={primaryButton}
              disclaimerNode={disclaimerNode}
            >
              {modal.inlineLinks?.length ? (
                <p className="ui-modal__body">
                  {renderBodyWithInlineLinks(modal.body, modal.inlineLinks)}
                </p>
              ) : modal.bodyLink && !modal.bodyBeforeLink ? (
                <p className="ui-modal__body">
                  {modal.body}
                  <a
                    href={modal.bodyLink.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => logPfsAnalyticsEvent({
                      event_name: EVENT_NAME.LINK_CLICK,
                      event_screen: EVENT_SCREEN.LOGIN,
                      link: { link_url: modal.bodyLink!.href, link_name: "eligibility_body_link" },
                    })}
                  >
                    {modal.bodyLink.label}
                  </a>
                </p>
              ) : (
                <>
                  {modal.body ? renderEligibilityBodyParagraphs(modal.body) : null}
                  {modal.bodyLink && (
                    <p className="ui-modal__body">
                      {modal.bodyBeforeLink ?? ""}
                      <a
                        href={modal.bodyLink.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => logPfsAnalyticsEvent({
                          event_name: EVENT_NAME.LINK_CLICK,
                          event_screen: EVENT_SCREEN.LOGIN,
                          link: { link_url: modal.bodyLink!.href, link_name: "eligibility_body_link" },
                        })}
                      >
                        {modal.bodyLink.label}
                      </a>
                      {modal.bodyAfterLink ?? ""}
                    </p>
                  )}
                </>
              )}
              <div className="ui-modal__icon-wrap">
                <img
                  src={ELIGIBILITY_MEGAPHONE_IMG_SRC}
                  alt=""
                  className="ui-modal__icon"
                  aria-hidden
                />
              </div>
              <div className="ui-modal__actions">
                <UIButton
                  onClick={primaryButton.onClick}
                  variant="primary"
                  fullWidth
                  showEndIcon
                >
                  {primaryButton.label}
                </UIButton>
              </div>
              {disclaimerNode && (
                <div className="ui-modal__disclaimer">{disclaimerNode}</div>
              )}
            </UIModal>
          );
        }

        const supportTextNode = modal.supportText && modal.supportInlineLinks?.length
          ? renderBodyWithInlineLinks(modal.supportText, modal.supportInlineLinks)
          : modal.supportText;

        return (
          <UIModal
            open={!!modal}
            onClose={closeEligibilityModal}
            title={modal.title}
            className="ui-modal--eligibility"
            body={modal.body || undefined}
            encouragementText={modal.encouragementText}
            supportText={supportTextNode}
            primaryButton={primaryButton}
            disclaimerNode={disclaimerNode}
          />
        );
      })()}
      {currentPopup && isSavingsStartPopup && savingsStartData && (
        <UIModal
          open={true}
          onClose={handleClose}
          title={t("savingsStartModal.title")}
          closable={false}
          width={760}
        >
          <SavingsStartModal
            remainingUsageAmount={savingsStartData.remainingUsageAmount}
            showGroupMailingCheckbox={
              !savingsStartData.groupMailingConfirmation
            }
            onConfirm={handleSavingsConfirm}
            onGroupMailingConsentChange={(consent) => {
              savingsConsentRef.current = consent;
            }}
          />
        </UIModal>
      )}
      {currentPopup && !isSavingsStartPopup && (
        <Modal {...getModalProps()}>
          <div className="popup-manager__content">
            {isTransferDisclaimerPopup && transferDisclaimerData ? (
              <TransferDisclaimerModal
                userName={transferDisclaimerData.userName}
                siteName={transferDisclaimerData.siteName}
                siteLogo={transferDisclaimerData.siteLogo}
                affiliateAggregatorName={
                  transferDisclaimerData.affiliateAggregatorName
                }
                limitAmount={transferDisclaimerData.limitAmount}
                websiteTimerServerMs={
                  transferDisclaimerData.websiteTimerServerMs
                }
                websiteTimerReceivedAt={
                  transferDisclaimerData.websiteTimerReceivedAt
                }
                onConfirmStart={transferDisclaimerData.onConfirmStart}
                onConfirm={(popup) => {
                  transferDisclaimerData.onConfirm(popup);
                  handleClose();
                }}
              />
            ) : (
              <>
                {currentPopup.message && (
                  <div className="popup-manager__message">
                    {currentPopup.message}
                  </div>
                )}
                {currentPopup.data?.content && (
                  <div className="popup-manager__custom-content">
                    {currentPopup.data.content}
                  </div>
                )}
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default PopupManager;
